import { EmptyState, Table, type TableColumn } from '@today/ui-components'
import { Button, Dialog } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import { ProductConfirmDialogView } from './product-confirm-dialog.view'
import { ErrorText, TextInput } from './product-fields'
import type { CategoryFormView, CategoryRowView, Translate } from './product-types'

/**
 * Props for {@link ProductCategoriesView}.
 *
 * @property rows - Every category in the API's order (sortOrder, then name).
 * @property editor - The open create/edit dialog, or null.
 * @property deleting - The category awaiting delete confirmation, or null.
 * @property onNew - Present only with `product.write` (also gates Edit).
 * @property onDelete - Present only with `product.delete`.
 */
export interface ProductCategoriesViewProps {
	t: Translate
	rows: CategoryRowView[]
	error: boolean
	editor: {
		mode: 'create' | 'edit'
		values: CategoryFormView
		errors: Record<string, string>
		formError: string | null
	} | null
	deleting: { name: string; error: string | null } | null
	isBusy: boolean
	onRetry: () => void
	onEditorChange: (field: keyof CategoryFormView, value: string) => void
	onEditorSubmit: () => void
	onEditorClose: () => void
	onConfirmDelete: () => void
	onCancelDelete: () => void
	onNew?: () => void
	onEdit?: (id: string) => void
	onDelete?: (id: string) => void
}

const columns = (props: ProductCategoriesViewProps): TableColumn<CategoryRowView>[] => {
	const { t } = props
	return [
		{ key: 'code', header: t('categories.columns.code') },
		{ key: 'name', header: t('categories.columns.name') },
		{ key: 'icon', header: t('categories.columns.icon'), cell: (row) => row.icon ?? '—' },
		{ key: 'sortOrder', header: t('categories.columns.sortOrder'), cell: (row) => String(row.sortOrder) },
		{
			key: 'actions',
			header: t('categories.columns.actions'),
			cell: (row) => (
				<div className="flex gap-2">
					{props.onEdit ? (
						<Button variant="muted" size="sm" onPress={() => props.onEdit?.(row.id)}>
							{t('categories.edit')}
						</Button>
					) : null}
					{props.onDelete ? (
						<Button variant="danger" size="sm" onPress={() => props.onDelete?.(row.id)}>
							{t('categories.delete')}
						</Button>
					) : null}
				</div>
			),
		},
	]
}

function EditorDialog({ props }: { props: ProductCategoriesViewProps }): ReactElement | null {
	const { t, editor } = props
	if (!editor) return null
	const field = (name: keyof CategoryFormView) => (
		<TextInput
			label={t(`categories.${name}`)}
			value={editor.values[name]}
			error={editor.errors[name]}
			onChange={(v) => props.onEditorChange(name, v)}
		/>
	)
	return (
		<Dialog
			isOpen
			onOpenChange={(open) => (open ? undefined : props.onEditorClose())}
			title={t(editor.mode === 'create' ? 'categories.dialogNew' : 'categories.dialogEdit')}
		>
			<div className="flex flex-col gap-3">
				{field('code')}
				{field('name')}
				{field('icon')}
				{field('sortOrder')}
				<ErrorText message={editor.formError} />
				<Button isDisabled={props.isBusy} onPress={props.onEditorSubmit}>
					{t('categories.save')}
				</Button>
			</div>
		</Dialog>
	)
}

/** Category management: ordered table, create/edit dialog and guarded delete (organism). */
export function ProductCategoriesView(props: ProductCategoriesViewProps): ReactElement {
	const { t } = props
	let body: ReactElement
	if (props.error) {
		body = <EmptyState title={t('categories.error')} action={{ label: t('actions.retry'), onPress: props.onRetry }} />
	} else if (props.rows.length === 0) {
		body = <EmptyState title={t('categories.empty')} />
	} else {
		body = <Table columns={columns(props)} rows={props.rows} />
	}
	return (
		<section className="flex flex-col gap-4">
			<header className="flex items-center justify-between">
				<h1 className="text-ds-xl font-semibold text-ds-text">{t('categories.title')}</h1>
				{props.onNew ? <Button onPress={props.onNew}>{t('categories.new')}</Button> : null}
			</header>
			{body}
			<EditorDialog props={props} />
			<ProductConfirmDialogView
				t={t}
				isOpen={props.deleting !== null}
				title={t('categories.deleteTitle')}
				body={t('categories.deleteBody', { name: props.deleting?.name ?? '' })}
				confirmLabel={t('categories.delete')}
				danger
				error={props.deleting?.error}
				onConfirm={props.onConfirmDelete}
				onCancel={props.onCancelDelete}
			/>
		</section>
	)
}
